import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
// import Button from '../../../../components/common/Button/index'
import Select from '../../../../components/common/Select'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import useAppSelector from '../../../../state/redux/hooks/index'
import { fetchCustomers } from '../../../../state/redux/actions/customerActions'
import { fetchAgents } from '../../../../state/redux/actions/agentActions'
import MiniHeader from '../../../../components/layout/MiniHeader'
import { List, ListItemButton, ListItemAvatar, ListItemText, Avatar } from '@mui/material'
import Input from '../../../../components/common/Input/Input'
import { MdSearch } from 'react-icons/md'
import { formatDateDashed } from '../../../../shared/utils/dateUtils'

const UserList = ({ onEdit }) => {
  const [sortValue, setSortValue] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [query, setQuery] = useState('')
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)
  // const searchParams = useState(['full_name', 'email', 'region'])
  const { agents, loading: agentsLoading, error: agentsError } = useAppSelector(state => state.agent)
  const { customers, loading: customersLoading, error: customersError } = useAppSelector(state => state.customer)
  const dispatch = useDispatch()

  const loading = agentsLoading || customersLoading
  const error = agentsError || customersError

  useEffect(() => {
    dispatch(fetchAgents())
    dispatch(fetchCustomers())
  }, [dispatch])

  const allUsers = useMemo(() => [...agents, ...customers], [agents, customers])
  const [searchResults, setSearchResults] = useState([])
  const sortOptions = [
    { value: 'All', label: 'Sort by' },
    { value: 'new', label: 'New' },
    { value: 'old', label: 'Old' },
  ]
  const filterOptions = [
    { value: 'all', label: 'Region' },
    { value: 'Birmingham', label: 'Birmingham' },
    { value: 'Cardiff', label: 'Cardiff' },
    { value: 'Leeds', label: 'Leeds' },
    { value: 'Liverpool', label: 'Liverpool' },
    { value: 'Manchester', label: 'Manchester' },
  ]

  const handleSearch = useCallback(() => {
    if (query.trim() === '') {
      console.log('Search query is empty.')
      setSearchResults(allUsers)
      return
    }

    const results = allUsers.filter(user => {
      const searchParams = [user.full_name, user.email, user.region]
      return searchParams.some(param => param && param.toLowerCase().includes(query.toLowerCase()))
    })
    console.log('Search results:', results)
    setSearchResults(results)
  }, [allUsers, query])

  // Filter users by date range
  const filterUsersByDate = (users, startDate, endDate) => {
    if (!startDate || !endDate || !users || !Array.isArray(users)) {
      return users || []
    }

    return users.filter(user => {
      const userDate = new Date(user.createdAt || user.signupDate)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return userDate >= start && userDate <= end
    })
  }

  const filteredUsers = useMemo(() => {
    let result = [...allUsers]

    // Apply date filter first if actively filtering
    if (isFiltering && dateFilter) {
      result = filterUsersByDate(result, dateFilter.startDate, dateFilter.endDate)
    }

    // Apply region filter
    if (filterValue !== 'all' && filterValue !== '') {
      result = result.filter(user => user.region === filterValue)
      console.log('result AFTER REGION FILTER:', result)
    }

    // Apply sort
    if (sortValue) {
      result.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.signupDate)
        const dateB = new Date(b.createdAt || b.signupDate)
        return sortValue === 'new' ? dateB - dateA : dateA - dateB
      })
    }
    return result
  }, [allUsers, filterValue, sortValue, dateFilter, isFiltering])

  const handleFilterChange = e => {
    const value = e.target.value
    setFilterValue(value)
    console.log('filterValue in handleFilterChange:', value)
  }

  const handleSortChange = e => {
    const value = e.target.value
    setSortValue(value)
    console.log('sortValue in handleSortChange:', value)
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
  }

  return (
    <>
      <MiniHeader
        title="User Management"
        data={allUsers}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="relative p-8 w-[1040px] ml-[250px]">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && allUsers.length === 0 && (
          <Card variant="outlined" className="text-center p-8 border border-border rounded max-w-md w-full mt-[140px]">
            <Typography variant="body" className="text-secondary">
              No users found. Add new users to manage accounts.
            </Typography>
          </Card>
        )}
        {!loading && !error && allUsers.length > 0 && (
          <>
            <div className="flex justify-between items-center">
              {/* search input */}
              <div className="flex gap-2 items-center">
                <Input
                  id="search-form"
                  type="search"
                  name="search-form"
                  value={query}
                  placeholder="Search"
                  icon={<MdSearch />}
                  onChange={e => {
                    setQuery(e.target.value)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                    console.log(e.key)
                  }}
                  className="cursor-pointer"
                ></Input>
              </div>
              {/* sort and filter */}
              <div className="flex gap-4 items-center my-4">
                <div className="flex">
                  <Select options={sortOptions} value={sortValue} onChange={handleSortChange} className="" />
                </div>
                <div className="flex items-center gap-2">
                  <Select options={filterOptions} value={filterValue} onChange={handleFilterChange} className="" />
                </div>
              </div>
            </div>
            <div className="bg-white">
              <List className="px-4">
                <div className="grid grid-cols-5 gap-2">
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="left" className="px-4">
                        Name
                      </Typography>
                    }
                    className="col-span-2"
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Email
                      </Typography>
                    }
                    className=""
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="" className="">
                        Sign Up Date
                      </Typography>
                    }
                    className=""
                  />
                  <ListItemText
                    primary={
                      <Typography variant="h5" align="left" className="">
                        Location
                      </Typography>
                    }
                    className=""
                  />
                </div>
                {query.trim() === ''
                  ? filteredUsers.map(user => (
                      <ListItemButton key={user._id} className="grid grid-cols-5 gap-2">
                        <div className="flex col-span-2">
                          <ListItemAvatar>
                            <Avatar alt="Profile Picture" src={'/assets/images/house.jpg'} loading="eager" />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="h6" align="left" className="font-normal">
                                {user.full_name}
                              </Typography>
                            }
                          />
                        </div>
                        <ListItemText
                          primary={
                            <Typography variant="caption" align="left" className="font-normal">
                              {user.email.length > 15 ? `${user.email.slice(0, 15)}...` : user.email}
                            </Typography>
                          }
                        />
                        <ListItemText
                          primary={
                            <Typography variant="h6" align="left" className="font-normal">
                              {formatDateDashed(user.createdAt || user.signupDate)}
                            </Typography>
                          }
                          className="px-2"
                        />
                        <ListItemText
                          primary={
                            <Typography variant="h6" align="left" className="font-normal">
                              {user.region || user.location?.city || 'unavailable'}
                            </Typography>
                          }
                          className="px-2"
                        />
                      </ListItemButton>
                    ))
                  : searchResults.map(user => (
                      <ListItemButton
                        key={user._id}
                        className="grid grid-cols-5 gap-2"
                        onClick={() => onEdit(user._id)}
                      >
                        <div className="flex col-span-2">
                          <ListItemAvatar>
                            <Avatar alt="Profile Picture" src={'/assets/images/house.jpg'} loading="eager" />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="h6" align="left" className="font-normal">
                                {user.full_name}
                              </Typography>
                            }
                          />
                        </div>
                        <ListItemText
                          primary={
                            <Typography variant="caption" align="left" className="">
                              {user.email.length > 15 ? `${user.email.slice(0, 15)}...` : user.email}
                            </Typography>
                          }
                        />
                        <ListItemText
                          primary={
                            <Typography variant="caption" align="left" className="">
                              {formatDateDashed(user.createdAt || user.signupDate)}
                            </Typography>
                          }
                          className="px-2"
                        />
                        <ListItemText
                          primary={
                            <Typography variant="h6" align="left" className="font-normal">
                              {user.region || user.location?.city || 'unavailable'}
                            </Typography>
                          }
                          className="px-2"
                        />
                      </ListItemButton>
                    ))}
              </List>
            </div>
          </>
        )}
      </div>
    </>
  )
}

UserList.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    })
  ),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default UserList
